import { useEffect, useState } from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAdminLanguage } from "@/i18n/AdminLanguage";
import { api, fmtErr } from "@/lib/api";

const LANG_LABEL = { spanish: "Spanish", english: "English", portuguese: "Portuguese" };

export default function AdminBookings() {
  const {tr:t}=useAdminLanguage();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/bookings")
      .then((r) => setBookings(r.data))
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm(t("Delete booking?"))) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success(t("Booking deleted"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="admin-bookings-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-syne text-xl font-extrabold tracking-tight">{t("Class bookings")}</h2>
          <p className="mt-1 text-xs text-white/40">{t("Everyone who booked a class through the site.")}</p>
        </div>
        <button
          data-testid="refresh-bookings-btn"
          onClick={load}
          className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white/60 transition-colors hover:border-flame hover:text-flame"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-white/40">
              <th className="px-4 py-3">{t("Name")}</th>
              <th className="px-4 py-3">{t("Contact")}</th>
              <th className="px-4 py-3">{t("Language")}</th>
              <th className="px-4 py-3">{t("Class")}</th>
              <th className="px-4 py-3">{t("Booked")}</th>
              <th className="px-4 py-3 text-right">{t("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`admin-booking-row-${b.id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-semibold">{b.name}</td>
                <td className="px-4 py-3 text-white/55">
                  <div>{b.email}</div>
                  {b.phone && <div className="text-xs text-white/35">{b.phone}</div>}
                </td>
                <td className="px-4 py-3">
                  <span className="rounded-full bg-flame/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-flame">
                    {t(LANG_LABEL[b.language] || b.language)}
                  </span>
                </td>
                <td className="px-4 py-3 text-white/55">{b.slot_label}</td>
                <td className="px-4 py-3 text-white/40">{new Date(b.created_at).toLocaleDateString("en-GB")}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end">
                    <button
                      data-testid={`delete-booking-${b.id}`}
                      onClick={() => remove(b.id)}
                      className="grid h-8 w-8 place-items-center rounded-full border border-white/15 text-white/60 transition-colors hover:border-red-500 hover:text-red-400"
                      aria-label="Delete booking"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && bookings.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-white/35">
                  {t("No bookings")}
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-white/35">
                  {t("Loading…")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
